/**
 * WebSocket connection manager for live audio chat.
 *
 * Handles bidirectional audio streaming with the backend.
 */

// Get WebSocket URL based on current location
// Uses the same host/port as the page (goes through Caddy reverse proxy)
function getWebSocketUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host // Includes port if non-standard
  return `${protocol}//${host}/api/v1/live`
}

// Message types from server
export type ServerMessageType =
  | 'audio'
  | 'transcript'
  | 'tool_start'
  | 'tool_result'
  | 'status'
  | 'error'
  | 'end'
  | 'ready'

export interface ServerMessage {
  type: ServerMessageType
  timestamp: number
  // Audio message
  audio?: string
  mime_type?: string
  // Transcript message
  text?: string
  role?: string
  isFinal?: boolean
  // Tool messages
  name?: string
  args?: Record<string, unknown>
  result?: unknown
  success?: boolean
  duration_ms?: number
  // Status/error messages
  message?: string
  error?: string
  // Ready message
  session_id?: string
  voice?: string
}

// Callbacks for live connection events
export interface LiveConnectionCallbacks {
  onReady?: (sessionId: string, voice: string) => void
  onAudio?: (audioB64: string, mimeType: string) => void
  onTranscript?: (text: string, role: string, isFinal: boolean) => void
  onToolStart?: (name: string, args: Record<string, unknown>) => void
  onToolResult?: (name: string, result: unknown, success: boolean, durationMs: number) => void
  onStatus?: (message: string) => void
  onError?: (error: string) => void
  onEnd?: () => void
  onConnected?: () => void
  onDisconnected?: () => void
}

export interface LiveConnectionOptions {
  /** Auth token for WebSocket connection */
  token: string
  /** Voice preference */
  voice?: string
  /** Reconnect on disconnect */
  autoReconnect?: boolean
  /** Max reconnect attempts */
  maxReconnectAttempts?: number
}

export class LiveConnection {
  private ws: WebSocket | null = null
  private callbacks: LiveConnectionCallbacks = {}
  private options: LiveConnectionOptions
  private reconnectAttempts = 0
  private isConnecting = false

  constructor(options: LiveConnectionOptions) {
    this.options = {
      autoReconnect: true,
      maxReconnectAttempts: 3,
      ...options,
    }
  }

  /**
   * Set event callbacks.
   */
  setCallbacks(callbacks: LiveConnectionCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks }
  }

  /**
   * Connect to the live WebSocket server.
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return
    }

    if (this.isConnecting) {
      return
    }

    this.isConnecting = true

    try {
      // Build URL with auth token and voice
      let url = `${getWebSocketUrl()}?token=${encodeURIComponent(this.options.token)}`
      if (this.options.voice) {
        url += `&voice=${encodeURIComponent(this.options.voice)}`
      }

      this.ws = new WebSocket(url)

      await new Promise<void>((resolve, reject) => {
        if (!this.ws) {
          reject(new Error('WebSocket not initialized'))
          return
        }

        this.ws.onopen = () => {
          this.reconnectAttempts = 0
          this.callbacks.onConnected?.()
          resolve()
        }

        this.ws.onerror = () => {
          reject(new Error('WebSocket connection failed'))
        }

        this.ws.onclose = (event) => {
          this.handleDisconnect(event)
        }

        this.ws.onmessage = (event) => {
          this.handleMessage(event)
        }
      })
    } finally {
      this.isConnecting = false
    }
  }

  /**
   * Disconnect from the server.
   */
  disconnect(): void {
    if (this.ws) {
      // Disable auto-reconnect for intentional disconnect
      this.options.autoReconnect = false
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
  }

  /**
   * Send audio chunk to the server.
   */
  sendAudio(pcmBuffer: ArrayBuffer): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    // Convert to base64
    const bytes = new Uint8Array(pcmBuffer)
    const base64 = btoa(String.fromCharCode(...bytes))

    this.ws.send(JSON.stringify({
      type: 'audio',
      audio: base64,
    }))
  }

  /**
   * Send text message (hybrid mode).
   */
  sendText(text: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    this.ws.send(JSON.stringify({
      type: 'text',
      text,
    }))
  }

  /**
   * Send activity state (speaking/listening).
   */
  sendActivity(state: 'speaking' | 'listening'): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    this.ws.send(JSON.stringify({
      type: 'activity',
      state,
    }))
  }

  /**
   * End the live session.
   */
  endSession(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return
    }

    this.ws.send(JSON.stringify({
      type: 'end',
    }))
  }

  /**
   * Check if connected.
   */
  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: ServerMessage = JSON.parse(event.data)

      switch (message.type) {
        case 'ready':
          this.callbacks.onReady?.(
            message.session_id || '',
            message.voice || 'Aoede'
          )
          break

        case 'audio':
          if (message.audio) {
            this.callbacks.onAudio?.(
              message.audio,
              message.mime_type || 'audio/pcm'
            )
          }
          break

        case 'transcript':
          if (message.text !== undefined) {
            this.callbacks.onTranscript?.(
              message.text,
              message.role || 'assistant',
              message.isFinal ?? true
            )
          }
          break

        case 'tool_start':
          if (message.name) {
            this.callbacks.onToolStart?.(
              message.name,
              message.args || {}
            )
          }
          break

        case 'tool_result':
          if (message.name) {
            this.callbacks.onToolResult?.(
              message.name,
              message.result,
              message.success ?? true,
              message.duration_ms ?? 0
            )
          }
          break

        case 'status':
          if (message.message) {
            this.callbacks.onStatus?.(message.message)
          }
          break

        case 'error':
          this.callbacks.onError?.(message.error || 'Unknown error')
          break

        case 'end':
          this.callbacks.onEnd?.()
          break
      }
    } catch (e) {
      console.warn('Failed to parse WebSocket message:', e)
    }
  }

  private handleDisconnect(event: CloseEvent): void {
    this.ws = null
    this.callbacks.onDisconnected?.()

    // Auto-reconnect if enabled and not a normal close
    if (
      this.options.autoReconnect &&
      event.code !== 1000 &&
      this.reconnectAttempts < (this.options.maxReconnectAttempts || 3)
    ) {
      this.reconnectAttempts++
      console.log(`Reconnecting... (attempt ${this.reconnectAttempts})`)
      setTimeout(() => {
        this.connect().catch((e) => {
          console.error('Reconnect failed:', e)
        })
      }, 1000 * this.reconnectAttempts) // Exponential backoff
    }
  }
}

/**
 * Create a new live connection.
 */
export function createLiveConnection(options: LiveConnectionOptions): LiveConnection {
  return new LiveConnection(options)
}

/**
 * Hook-friendly wrapper for creating live connections.
 */
export function useLiveConnection(
  token: string,
  callbacks: LiveConnectionCallbacks,
  voice?: string
): LiveConnection {
  const connection = new LiveConnection({ token, voice })
  connection.setCallbacks(callbacks)
  return connection
}
